import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import ShareModal from "./components/ShareModal";
import { getRelated, getSpecialsForWork, getTypeLabel, getWorkById } from "@/utils/media";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageSeo } from "@/hooks/usePageSeo";

const resourceList = [
  { id: "main", label: "正片", note: "HD", icon: "ri-movie-line" },
  { id: "trailer", label: "预告片", note: "1分30秒", icon: "ri-clapperboard-line" },
  { id: "extra", label: "花絮特辑", note: "幕后", icon: "ri-video-line" },
];

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const work = getWorkById(id);
  const { toggleFavorite, isFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");

  usePageSeo({
    title: work ? `${work.title} - 91av视频影视详情` : "影视详情 - 91av视频",
    description: work
      ? `${work.title}：${work.summary} 由 ${work.director} 执导，${work.actors.join("、")} 主演，${work.year} 年上映。`
      : "91av视频影视详情页。",
    keywords: work
      ? `91av视频,${work.title},${work.genres.join(",")},${work.region}影视`
      : "91av视频,影视详情",
  });

  if (!work) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-20 text-center md:px-8">
        <i className="ri-file-unknow-line text-5xl text-foreground-400" />
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">影片不存在</h1>
        <p className="mt-2 text-sm text-foreground-600">该影视详情不存在或链接已失效。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelated(work, 6);
  const specialsOf = getSpecialsForWork(work);
  const favorited = isFavorite(work.id);

  const showToast = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(""), 2600);
  };

  const metaRows = [
    { label: "导演", value: work.director },
    { label: "主演", value: work.actors.join(" / ") },
    { label: "类型", value: work.genres.join(" / ") },
    { label: "地区", value: work.region },
    { label: "语言", value: work.language },
    { label: "上映年份", value: String(work.year) },
    { label: "片长 / 集数", value: work.duration },
  ];

  return (
    <div className="w-full">
      <div className="relative h-[360px] w-full overflow-hidden md:h-[460px]">
        <img
          src={work.backdrop}
          alt={`${work.title} 剧照`}
          title={`${work.title} - 91av视频影视详情`}
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/60 to-foreground-950/30" />
        <div className="absolute inset-0">
          <div className="mx-auto flex h-full w-full max-w-[1280px] flex-col justify-end px-4 pb-8 md:px-8">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: getTypeLabel(work.type), to: `/category/${work.type}` },
                { label: work.title },
              ]}
            />
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-accent-500 px-2 py-0.5 text-xs font-bold text-background-50">
                {getTypeLabel(work.type)}
              </span>
              {work.hot && (
                <span className="flex items-center gap-1 rounded-md bg-foreground-950/50 px-2 py-0.5 text-xs font-semibold text-primary-100 backdrop-blur">
                  <i className="ri-fire-line" /> 热门
                </span>
              )}
              <span className="flex items-center gap-1 rounded-md bg-secondary-500 px-2 py-0.5 text-xs font-bold text-background-50">
                <i className="ri-star-fill" /> {work.rating.toFixed(1)}
              </span>
            </div>
            <h1 className="mt-3 font-heading text-3xl font-bold text-background-50 md:text-5xl">
              {work.title}
            </h1>
            <p className="mt-2 text-sm text-background-100/85">
              {work.year} · {work.region} · {work.genres.join(" / ")} · {work.duration}
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr]">
          <aside className="flex flex-col gap-4">
            <div className="overflow-hidden rounded-lg border border-background-200/70">
              <div className="aspect-[2/3] w-full bg-background-200">
                <img
                  src={work.poster}
                  alt={`${work.title} 海报`}
                  title={`${work.title} - 91av视频影视资源`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const nowFav = toggleFavorite(work.id);
                  showToast(nowFav ? "已收藏到本地列表" : "已取消收藏");
                }}
                className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
                  favorited
                    ? "bg-secondary-500 text-background-50 hover:bg-secondary-600"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
            </div>
            <p className="text-xs leading-relaxed text-foreground-400">
              收藏仅保存在你的浏览器本地，用于演示收藏与标记效果。
            </p>
          </aside>

          <div className="flex flex-col gap-6">
            <section className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-information-line text-primary-700" />
                影片资料
              </h2>
              <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="w-20 shrink-0 text-foreground-500">{row.label}</dt>
                    <dd className="flex-1 text-foreground-900">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-file-text-line text-primary-700" />
                剧情简介
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">{work.summary}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {work.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-play-circle-line text-primary-700" />
                  视频资源
                </h2>
                <span className="flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
                  <i className="ri-error-warning-line" />
                  演示状态 · 暂未接入播放
                </span>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {resourceList.map((res) => (
                  <button
                    key={res.id}
                    type="button"
                    onClick={() => showToast("播放功能暂未接入，当前为演示按钮")}
                    className="group flex items-center justify-between gap-2 rounded-lg border border-background-200/70 bg-background-100/60 px-4 py-3 text-left transition-colors hover:border-primary-300"
                  >
                    <span className="flex items-center gap-2">
                      <i className={`${res.icon} text-lg text-primary-700`} />
                      <span className="flex flex-col">
                        <span className="text-sm font-semibold text-foreground-900">{res.label}</span>
                        <span className="text-xs text-foreground-500">{res.note}</span>
                      </span>
                    </span>
                    <i className="ri-play-fill text-xl text-accent-500 opacity-60 transition-opacity group-hover:opacity-100" />
                  </button>
                ))}
              </div>
              <p className="mt-3 text-xs text-foreground-400">
                本站为内容演示站点，未接入真实播放接口，点击资源按钮将提示演示状态。
              </p>
            </section>

            <section className="rounded-lg border border-background-200/70 bg-background-100/60 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-links-line text-primary-700" />
                所属分类与专题
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to={`/category/${work.type}`}
                  className="flex items-center gap-1 rounded-md border border-background-300/70 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-folder-line" />
                  {getTypeLabel(work.type)}频道
                </Link>
                {work.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/search?q=${encodeURIComponent(g)}`}
                    className="flex items-center gap-1 rounded-md border border-background-300/70 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-secondary-300 hover:text-secondary-800"
                  >
                    <i className="ri-price-tag-3-line" />
                    {g}
                  </Link>
                ))}
                {specialsOf.map((sp) => (
                  <Link
                    key={sp.id}
                    to={`/special/${sp.id}`}
                    className="flex items-center gap-1 rounded-md bg-primary-100 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
                  >
                    <i className="ri-bookmark-3-line" />
                    {sp.title}
                  </Link>
                ))}
              </div>
            </section>
          </div>
        </div>

        <section className="mt-12">
          <div className="mb-5 flex items-center gap-2">
            <i className="ri-movie-2-line text-xl text-accent-600" />
            <h2 className="font-heading text-lg font-bold text-foreground-950">相关推荐</h2>
            <span className="text-xs text-foreground-500">同类型或同主演作品</span>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
            {related.map((item) => (
              <WorkCard key={item.id} work={item} />
            ))}
          </div>
        </section>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={work.title} />

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[80] -translate-x-1/2 rounded-full bg-foreground-950/90 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}